import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import useGetProfile from '@/hooks/useGetProfile';
import { useAuth0 } from '@auth0/auth0-react';
import { FileText, HelpCircle, Mail, Phone, Globe, Award } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import Faq from './Faq';
import LoadingModal from './LoadingModal';
import PropertyForm from './PropertyForm';
import agentProfile from '@/assets/agent-profile.jpg';
import mortgageLogo from '@/assets/mortgage-logo.jpg';

const HomePartner = () => {
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const { isLoading, handleRedirectCallback } = useAuth0();
  const location = useLocation();

  const firstName = query.get('firstName');
  const lastName = query.get('lastName');

  useEffect(() => {
    if (firstName && lastName && !isLoading) {
      window.localStorage.setItem(
        'user',
        JSON.stringify({ firstName, lastName }),
      );
      window.localStorage.setItem('user_type', 'USER');
    }
  }, [firstName, lastName, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      (async () => {
        const callback = await handleRedirectCallback();
        if (callback?.appState?.returnTo) navigate(callback.appState.returnTo);
      })();
    }
  }, [isLoading]);

  // Add effect to scroll to property form if hash is present
  useEffect(() => {
    if (location.hash === '#property-form') {
      const element = document.getElementById('property-form-section');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    }
  }, [location]);

  if (isLoading)
    return (
      <div>
        <LoadingModal show />
      </div>
    );

  return (
    <div className="container mx-auto px-4 py-8 relative">
      {/* Partner Branding Section - Desktop Only (Top) */}
      <div className="hidden md:block bg-gradient-to-r from-tax-lightGray/50 to-white border border-tax-borderGray rounded-lg shadow-sm mb-4 p-4">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Real Estate Agent - Courtesy Of */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <Avatar className="h-12 w-12 flex-shrink-0 border border-primary">
                  <AvatarImage src={agentProfile} alt="Sarah Mitchell" />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    SM
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    COURTESY OF
                  </p>
                  <h3 className="font-semibold text-sm text-foreground truncate">
                    Sarah Mitchell
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">
                    Premier Realty Group
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Phone className="h-2.5 w-2.5" />
                    <span className="truncate">(555) 123-4567</span>
                  </div>
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <Avatar className="h-16 w-16 border-2 border-primary">
                  <AvatarImage src={agentProfile} alt="Sarah Mitchell" />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    SM
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">Sarah Mitchell</h4>
                    <p className="text-sm text-muted-foreground">
                      Premier Realty Group
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <a
                        href="tel:5551234567"
                        className="hover:text-primary transition-colors"
                      >
                        (555) 123-4567
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      <a
                        href="mailto:sarah@premierrealty.com"
                        className="hover:text-primary transition-colors"
                      >
                        sarah@premierrealty.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-3.5 w-3.5" />
                      <a
                        href="https://www.premierrealty.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        premierrealty.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Award className="h-3.5 w-3.5" />
                      <span>License #RE-2024-5678</span>
                    </div>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>

          {/* Mortgage Company - Sponsored By */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                  <img
                    src={mortgageLogo}
                    alt="Summit Financial"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    SPONSORED BY
                  </p>
                  <h3 className="font-semibold text-sm text-foreground truncate">
                    Michael Rodriguez
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">
                    Summit Financial
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Phone className="h-2.5 w-2.5" />
                    <span className="truncate">(555) 987-6543</span>
                  </div>
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                  <img
                    src={mortgageLogo}
                    alt="Summit Financial"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">
                      Michael Rodriguez
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Summit Financial Services
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <a
                        href="tel:5559876543"
                        className="hover:text-primary transition-colors"
                      >
                        (555) 987-6543
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      <a
                        href="mailto:michael@summitfinancial.com"
                        className="hover:text-primary transition-colors"
                      >
                        michael@summitfinancial.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-3.5 w-3.5" />
                      <a
                        href="https://www.summitfinancial.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        summitfinancial.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Award className="h-3.5 w-3.5" />
                      <span>NMLS #1234567</span>
                    </div>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>

          {/* Company - Brought to you by */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                  <Globe className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    BROUGHT TO YOU BY
                  </p>
                  <h3 className="font-semibold text-sm text-foreground">
                    MortgageNews.org
                  </h3>
                  <a
                    href="https://www.mortgagenews.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-1"
                  >
                    <Globe className="h-2.5 w-2.5" />
                    <span className="truncate">mortgagenews.org</span>
                  </a>
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                  <Globe className="h-10 w-10 text-primary" />
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">
                      MortgageNews.org
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Your Source for Mortgage Industry News
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-muted-foreground text-xs leading-relaxed">
                      The latest news, trends, and insights in the mortgage and
                      real estate industry.
                    </p>
                    <div className="flex items-center gap-2 text-muted-foreground pt-1">
                      <Globe className="h-3.5 w-3.5" />
                      <a
                        href="https://www.mortgagenews.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors font-medium"
                      >
                        Visit mortgagenews.org
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
      </div>

      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-bold tracking-tight mb-6">
            Protest Your Property Tax Valuation
          </h1>
          <p className="text-lg md:text-xl text-tax-neutralGray mb-8 max-w-2xl mx-auto">
            Find out if your home has been overvalued and learn how to appeal
            your county tax assessment. 100% free to use.
          </p>

          {/* Moved "How Property Tax Appeals Work" section here */}
          <div className="py-8 bg-tax-lightGray rounded-lg mb-8">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-3xl font-semibold text-center mb-12">
                How Property Tax Appeals Work
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-tax-borderGray">
                  <div className="h-12 w-12 bg-[#D32F2F] rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-center text-tax-deepBlue">
                    Find Your Property
                  </h3>
                  <p className="text-tax-neutralGray text-center">
                    Enter your address below and we'll retrieve all the
                    pertinent data necessary to start an appeal.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-tax-borderGray">
                  <div className="h-12 w-12 bg-[#D32F2F] rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-center text-tax-deepBlue">
                    Build Your Case
                  </h3>
                  <p className="text-tax-neutralGray text-center">
                    We'll help you find comparable sales data necessary to argue
                    your case for a lower value.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-tax-borderGray">
                  <div className="h-12 w-12 bg-[#D32F2F] rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold text-xl">3</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-center text-tax-deepBlue">
                    File Your Appeal
                  </h3>
                  <p className="text-tax-neutralGray text-center">
                    We'll give you a complete letter, report and instructions on
                    submitting it to your local assessor.
                  </p>
                </div>
              </div>

              {/* Removed "Learn More About The Process" button */}
            </div>
          </div>

          <div id="property-form-section" className="mb-8 scroll-mt-24">
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="h-8 w-8 bg-[#D32F2F] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-md">1</span>
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  ENTER YOUR PROPERTY ADDRESS
                </h3>
              </div>
              <PropertyForm />
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-semibold text-center mb-4">
            Why Appeal Your Property Taxes?
          </h2>
          <p className="text-lg text-center text-tax-neutralGray mb-12 max-w-2xl mx-auto">
            Many property owners are paying more than they should due to county
            overvaluations.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <FileText className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  County Errors Are Common
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                Mass appraisal techniques often miss important details about
                your specific property, leading to overvaluation and higher tax
                bills.
              </p>
            </div>

            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <HelpCircle className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  Save Money Every Year
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                A successful appeal can lower your property tax bill for the
                current year and potentially set a precedent for future
                assessments.
              </p>
            </div>
          </div>

          {/* Tax Appeal Guidance component has been moved to PropertyForm component */}
        </div>
      </section>

      <section className="py-12 md:py-16 bg-tax-lightGray rounded-lg">
        <div className="max-w-4xl mx-auto px-4">
          <Faq />

          <div className="mt-10 text-center">
            <Button
              asChild
              variant="outline"
              className="max-w-xs min-h-[3rem] px-6 bg-primary border-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground rounded-lg"
            >
              <Link to="/faq">View All FAQs</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Partner Branding Section - Mobile Only (Bottom) */}
      <div className="block md:hidden bg-gradient-to-r from-tax-lightGray/50 to-white border border-tax-borderGray rounded-lg shadow-sm mt-8 p-4">
        <div className="grid gap-4">
          {/* Real Estate Agent - Courtesy Of */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <Avatar className="h-12 w-12 flex-shrink-0 border border-primary">
                  <AvatarImage src={agentProfile} alt="Sarah Mitchell" />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    SM
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    COURTESY OF
                  </p>
                  <h3 className="font-semibold text-sm text-foreground truncate">
                    Sarah Mitchell
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">
                    Premier Realty Group
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Phone className="h-2.5 w-2.5" />
                    <span className="truncate">(555) 123-4567</span>
                  </div>
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <Avatar className="h-16 w-16 border-2 border-primary">
                  <AvatarImage src={agentProfile} alt="Sarah Mitchell" />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    SM
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">Sarah Mitchell</h4>
                    <p className="text-sm text-muted-foreground">
                      Premier Realty Group
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <a
                        href="tel:5551234567"
                        className="hover:text-primary transition-colors"
                      >
                        (555) 123-4567
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      <a
                        href="mailto:sarah@premierrealty.com"
                        className="hover:text-primary transition-colors"
                      >
                        sarah@premierrealty.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-3.5 w-3.5" />
                      <a
                        href="https://www.premierrealty.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        premierrealty.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Award className="h-3.5 w-3.5" />
                      <span>License #RE-2024-5678</span>
                    </div>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>

          {/* Mortgage Company - Sponsored By */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                  <img
                    src={mortgageLogo}
                    alt="Summit Financial"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    SPONSORED BY
                  </p>
                  <h3 className="font-semibold text-sm text-foreground truncate">
                    Michael Rodriguez
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">
                    Summit Financial
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Phone className="h-2.5 w-2.5" />
                    <span className="truncate">(555) 987-6543</span>
                  </div>
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                  <img
                    src={mortgageLogo}
                    alt="Summit Financial"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">
                      Michael Rodriguez
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Summit Financial Services
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <a
                        href="tel:5559876543"
                        className="hover:text-primary transition-colors"
                      >
                        (555) 987-6543
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      <a
                        href="mailto:michael@summitfinancial.com"
                        className="hover:text-primary transition-colors"
                      >
                        michael@summitfinancial.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-3.5 w-3.5" />
                      <a
                        href="https://www.summitfinancial.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        summitfinancial.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Award className="h-3.5 w-3.5" />
                      <span>NMLS #1234567</span>
                    </div>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>

          {/* Company - Brought to you by */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                  <Globe className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    BROUGHT TO YOU BY
                  </p>
                  <h3 className="font-semibold text-sm text-foreground">
                    MortgageNews.org
                  </h3>
                  <a
                    href="https://www.mortgagenews.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-1"
                  >
                    <Globe className="h-2.5 w-2.5" />
                    <span className="truncate">mortgagenews.org</span>
                  </a>
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                  <Globe className="h-10 w-10 text-primary" />
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">
                      MortgageNews.org
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Your Source for Mortgage Industry News
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-muted-foreground text-xs leading-relaxed">
                      The latest news, trends, and insights in the mortgage and
                      real estate industry.
                    </p>
                    <div className="flex items-center gap-2 text-muted-foreground pt-1">
                      <Globe className="h-3.5 w-3.5" />
                      <a
                        href="https://www.mortgagenews.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors font-medium"
                      >
                        Visit mortgagenews.org
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
      </div>
    </div>
  );
};
export default HomePartner;
